import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarIcon,
  CheckCircledIcon,
  ChevronDownIcon,
  CopyIcon,
  Cross2Icon,
  ResetIcon,
} from '@radix-ui/react-icons'
import { Logo } from '@repo/ui/components/logo'
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/components/ui/avatar'
import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/components/ui/card'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import { Skeleton } from '@repo/ui/components/ui/skeleton'
import { Switch } from '@repo/ui/components/ui/switch'
import { Textarea } from '@repo/ui/components/ui/textarea'
import { surfaceRadii, surfaceSpacing } from '@repo/ui/lib/design-tokens'
import { cn } from '@repo/ui/lib/utils'
import { useRouter } from '@tanstack/react-router'
import { ArrowRight, CheckCircle2, LockKeyhole, RefreshCw, Trash2 } from 'lucide-react'
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  type BusinessProfileDraft,
  BusinessProfileEditor,
} from '@/components/operator/business-profile-editor'
import {
  buildOnboardingCatalogInput,
  createOnboardingServiceDraft,
  createPreviewServiceRecords,
  createServiceDraftsFromCatalog,
  createStarterServiceDrafts,
  formatCentsForInput,
  getServiceDraftReadiness,
  type OnboardingServiceDraft,
  parseCurrencyToCents,
  STARTER_STANDARD_VISIT_PRICE_CENTS,
  slugifyServiceValue,
} from '@/components/operator-onboarding/onboarding-services'
import {
  CoveragePreviewCard,
  type CoveragePreviewDraft,
  ServiceFootprintEditor,
} from '@/components/operator-onboarding/service-footprint-editor'
import {
  getInitialSetupWizardStep,
  getSetupWizardNextAction,
  type SetupWizardStepId,
} from '@/components/operator-onboarding/setup-wizard'
import { PremiumPanel } from '@/components/ui/premium-panel'
import { FORM, LAYOUT, PREVIEW, SHELL, SURFACE, TIMELINE } from '@/lib/design/onboarding'
import { formatPhoneDisplay } from '@/lib/format-phone'
import type {
  AvailabilitySlotRecord,
  OperatorConfigurationState,
  OperatorProfile,
  ReadinessModel,
  ServiceRecord,
} from '@/lib/operator-config/types'
import { trpc } from '@/lib/trpc'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const setupPanelClass = 'rounded-[4px] border-border'
const insetPanelClass = cn(
  'rounded-[4px]',
  'border border-border bg-background',
  surfaceSpacing.inset.default
)
const stepStackClass = surfaceSpacing.stack.default
const cardSectionStackClass = surfaceSpacing.stack.default

type SetupWizardStepConfig = {
  id: SetupWizardStepId
  title: string
  railLabel: string
  description: string
}

const WIZARD_STEPS: SetupWizardStepConfig[] = [
  {
    id: 'business',
    title: 'Your business',
    railLabel: 'Business',
    description: 'Name, link, and customer-facing details.',
  },
  {
    id: 'coverage',
    title: 'Where you work',
    railLabel: 'Area',
    description: 'Coverage, travel rules, and local boundaries.',
  },
  {
    id: 'availability',
    title: 'Hours',
    railLabel: 'Hours',
    description: 'Weekly windows customers can book.',
  },
  {
    id: 'services',
    title: 'Services',
    railLabel: 'Services',
    description: 'Offerings, duration, and pricing.',
  },
  {
    id: 'integrations',
    title: 'Explore your workspace',
    railLabel: 'Explore',
    description: 'Connect calendars for launch, or keep testing privately.',
  },
]

function SetupStepRail({
  currentStepId,
  furthestStepIndex,
  stepComplete,
  onStepSelect,
}: {
  currentStepId: SetupWizardStepId
  furthestStepIndex: number
  stepComplete: Record<SetupWizardStepId, boolean>
  onStepSelect: (step: SetupWizardStepId) => void
}) {
  return (
    <nav aria-label="Setup steps">
      <div className="grid grid-cols-2 border-b border-border/60 sm:grid-cols-3 lg:grid-cols-5">
        {WIZARD_STEPS.map((stepConfig, index) => {
          const isCurrent = stepConfig.id === currentStepId
          const isComplete = stepComplete[stepConfig.id]
          const isReachable = index <= furthestStepIndex || isComplete

          return (
            <button
              key={stepConfig.id}
              type="button"
              disabled={!isReachable}
              aria-current={isCurrent ? 'step' : undefined}
              onClick={() => onStepSelect(stepConfig.id)}
              className={cn(
                'group relative inline-flex h-9 min-w-0 items-center justify-center gap-1.5 px-2 text-center text-xs font-medium transition-[color,opacity] duration-200 after:absolute after:inset-x-3 after:bottom-[-1px] after:h-0.5 after:origin-center after:scale-x-0 after:bg-primary after:transition-transform after:duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-35',
                isCurrent
                  ? 'text-primary after:scale-x-100'
                  : isComplete
                    ? 'text-foreground/80 after:scale-x-100 after:bg-primary/30 hover:text-primary hover:after:bg-primary/55'
                    : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {isComplete ? <CheckCircledIcon className="h-3 w-3 text-primary/75" /> : null}
              <span className="min-w-0 truncate">{stepConfig.railLabel}</span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}

function SetupShellBrandLabel() {
  return (
    <div className={SHELL.header.brandRow}>
      <div className={SHELL.header.brandPill}>
        <Logo className="h-6 w-6 text-foreground" />
        <span>Zippity</span>
      </div>
    </div>
  )
}

function SetupShellHeader({
  title,
  description,
  currentStepIndex,
  furthestStepIndex,
  stepComplete,
  onStepSelect,
}: {
  title: string
  description: string
  currentStepIndex: number
  furthestStepIndex: number
  stepComplete: Record<SetupWizardStepId, boolean>
  onStepSelect: (step: SetupWizardStepId) => void
}) {
  const currentStepId = WIZARD_STEPS[currentStepIndex]?.id ?? WIZARD_STEPS[0].id

  return (
    <header className={SHELL.header.root}>
      <div className={SHELL.header.inner}>
        <SetupShellBrandLabel />

        <div className={SHELL.header.titleRow}>
          <div className={SHELL.header.titleCopy}>
            <h1 className={SHELL.header.title}>{title}</h1>
            <p className={SHELL.header.description}>{description}</p>
          </div>
        </div>

        <SetupStepRail
          currentStepId={currentStepId}
          furthestStepIndex={furthestStepIndex}
          stepComplete={stepComplete}
          onStepSelect={onStepSelect}
        />
      </div>
    </header>
  )
}

export function SetupShellLoadingState() {
  return (
    <div className="p-6">
      <PremiumPanel className="mx-auto max-w-4xl border-border/70">
        <CardContent className={`${surfaceSpacing.inset.default} ${SHELL.header.inner}`}>
          <SetupShellBrandLabel />

          <div className={SHELL.header.titleRow}>
            <div className={cn(SHELL.header.titleCopy, 'space-y-3')}>
              <Skeleton className="h-10 w-40 rounded-[4px]" />
              <Skeleton className="h-4 w-full max-w-xl rounded-[3px]" />
            </div>
          </div>

          <div className="grid grid-cols-2 border-b border-border/60 sm:grid-cols-3 lg:grid-cols-5">
            {WIZARD_STEPS.map((stepConfig) => (
              <Skeleton key={stepConfig.id} className="mx-3 h-9 rounded-[3px]" />
            ))}
          </div>

          <Skeleton className="h-[24rem] rounded-[6px]" />
        </CardContent>
      </PremiumPanel>
    </div>
  )
}

type WeeklyWindow = {
  id: string
  startTime: string
  endTime: string
}

function ServiceUnitInput({
  id,
  value,
  onChange,
  type = 'text',
  min,
  inputMode,
  placeholder,
  prefix,
  suffix,
  ariaLabel,
}: {
  id: string
  value: string | number
  onChange: (value: string) => void
  type?: string
  min?: number
  inputMode?: 'decimal' | 'numeric'
  placeholder?: string
  prefix?: string
  suffix?: string
  ariaLabel?: string
}) {
  return (
    <div className="relative">
      {prefix ? (
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-medium text-muted-foreground">
          {prefix}
        </span>
      ) : null}
      <Input
        id={id}
        type={type}
        min={min}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={cn(
          FORM.input,
          'h-9 rounded-lg text-sm',
          prefix ? 'pl-7' : '',
          suffix ? 'pr-12' : ''
        )}
        onChange={(event) => onChange(event.target.value)}
      />
      {suffix ? (
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-muted-foreground">
          {suffix}
        </span>
      ) : null}
    </div>
  )
}

function formatCurrency(cents: number | null | undefined, currency = 'usd') {
  if (cents == null) return 'Contact for pricing'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

function toPreviewSlug(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function getInitials(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

function minutesBetween(startTime: string, endTime: string) {
  const [startHour, startMinute] = startTime.split(':').map(Number)
  const [endHour, endMinute] = endTime.split(':').map(Number)
  return endHour * 60 + endMinute - (startHour * 60 + startMinute)
}

const DAY_SHORT_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function buildInitialAvailabilityWindows(slots: AvailabilitySlotRecord[]) {
  return DAYS.map((_, dayOfWeek) =>
    slots
      .filter((slot) => slot.dayOfWeek === dayOfWeek)
      .map((slot) => ({
        id: slot.id,
        startTime: slot.startTime,
        endTime: slot.endTime,
      }))
  )
}

function buildInitialDayEnabled(slots: AvailabilitySlotRecord[]) {
  if (slots.length === 0) {
    return DAYS.map((_, dayOfWeek) => dayOfWeek >= 1 && dayOfWeek <= 5)
  }

  return DAYS.map((_, dayOfWeek) => slots.some((slot) => slot.dayOfWeek === dayOfWeek))
}

function formatCompactTimeLabel(value: string) {
  const [hourText, minuteText] = value.split(':')
  const hour = Number(hourText)
  const minute = Number(minuteText)
  const suffix = hour >= 12 ? 'pm' : 'am'
  const normalizedHour = hour % 12 || 12
  if (minute === 0) return `${normalizedHour}${suffix}`
  return `${normalizedHour}:${String(minute).padStart(2, '0')}${suffix}`
}

function summarizeDayWindows(dayWindows: WeeklyWindow[]) {
  if (dayWindows.length === 0) return 'Off'
  if (dayWindows.length === 1) {
    return `${formatCompactTimeLabel(dayWindows[0].startTime)}–${formatCompactTimeLabel(dayWindows[0].endTime)}`
  }
  return 'Split hours'
}

const TIMELINE_START_HOUR = 0
const TIMELINE_END_HOUR = 24
const TIMELINE_SLOT_MINUTES = 30
const TIMELINE_SLOT_COUNT = ((TIMELINE_END_HOUR - TIMELINE_START_HOUR) * 60) / TIMELINE_SLOT_MINUTES
const TIMELINE_VISIBLE_HOURS = 12
const TIMELINE_VIEWPORT_WIDTH = '100%'
const TIMELINE_CANVAS_WIDTH_MULTIPLIER =
  (TIMELINE_END_HOUR - TIMELINE_START_HOUR) / TIMELINE_VISIBLE_HOURS
const TIMELINE_WIDTH_STYLE = {
  '--timeline-viewport-width': TIMELINE_VIEWPORT_WIDTH,
  '--timeline-canvas-width': `calc(var(--timeline-viewport-width) * ${TIMELINE_CANVAS_WIDTH_MULTIPLIER})`,
} as CSSProperties

function timeToMinutes(value: string) {
  const [hourText, minuteText] = value.split(':')
  return Number(hourText) * 60 + Number(minuteText)
}

function minutesToTime(value: number) {
  const hour = Math.floor(value / 60)
  const minute = value % 60
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function timeToTimelineSlotIndex(value: string) {
  const startMinutes = TIMELINE_START_HOUR * 60
  const endMinutes = TIMELINE_END_HOUR * 60
  const clampedMinutes = Math.min(Math.max(timeToMinutes(value), startMinutes), endMinutes)
  return Math.round((clampedMinutes - startMinutes) / TIMELINE_SLOT_MINUTES)
}

function timelineSlotIndexToTime(index: number) {
  const startMinutes = TIMELINE_START_HOUR * 60
  const clampedIndex = Math.min(Math.max(index, 0), TIMELINE_SLOT_COUNT)
  return minutesToTime(startMinutes + clampedIndex * TIMELINE_SLOT_MINUTES)
}

function createWeeklyWindow(dayOfWeek: number, startTime: string, endTime: string): WeeklyWindow {
  return {
    id: `${dayOfWeek}-${startTime}-${endTime}-${crypto.randomUUID()}`,
    startTime,
    endTime,
  }
}

function normalizeDayWindows(dayWindows: WeeklyWindow[]) {
  const sorted = [...dayWindows]
    .filter((window) => timeToMinutes(window.endTime) > timeToMinutes(window.startTime))
    .sort((left, right) => left.startTime.localeCompare(right.startTime))

  return sorted.reduce<WeeklyWindow[]>((result, window) => {
    const previous = result[result.length - 1]
    if (!previous) {
      result.push(window)
      return result
    }

    const previousEnd = timeToMinutes(previous.endTime)
    const currentStart = timeToMinutes(window.startTime)
    const currentEnd = timeToMinutes(window.endTime)

    if (currentStart <= previousEnd) {
      previous.endTime = minutesToTime(Math.max(previousEnd, currentEnd))
      return result
    }

    result.push(window)
    return result
  }, [])
}

function formatTimelineHourLabel(index: number) {
  return formatCompactTimeLabel(`${String(TIMELINE_START_HOUR + index).padStart(2, '0')}:00`)
}

function getTimelineWindowStyle(window: WeeklyWindow) {
  const startIndex = timeToTimelineSlotIndex(window.startTime)
  const endIndex = timeToTimelineSlotIndex(window.endTime)
  const left = (startIndex / TIMELINE_SLOT_COUNT) * 100
  const width = (Math.max(endIndex - startIndex, 1) / TIMELINE_SLOT_COUNT) * 100

  return {
    left: `${left}%`,
    width: `${width}%`,
  }
}

function getDraftTimelineStyle(startSlot: number, endSlot: number) {
  const leftSlot = Math.min(startSlot, endSlot)
  const rightSlot = Math.max(startSlot, endSlot) + 1
  return {
    left: `${(leftSlot / TIMELINE_SLOT_COUNT) * 100}%`,
    width: `${((rightSlot - leftSlot) / TIMELINE_SLOT_COUNT) * 100}%`,
  }
}

function getInitialPreviewDate(slots: AvailabilitySlotRecord[]): Date | null {
  const activeDays = new Set(slots.map((slot) => slot.dayOfWeek))
  if (activeDays.size === 0) return null

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  for (let offset = 0; offset < 45; offset += 1) {
    const candidate = new Date(today)
    candidate.setDate(today.getDate() + offset)
    if (activeDays.has(candidate.getDay())) {
      return candidate
    }
  }

  return null
}

function startOfPreviewWeek(date: Date) {
  const weekStart = new Date(date)
  weekStart.setHours(0, 0, 0, 0)
  const offset = (weekStart.getDay() + 6) % 7
  weekStart.setDate(weekStart.getDate() - offset)
  return weekStart
}

function buildPreviewWeekDates(weekStart: Date) {
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(weekStart)
    date.setDate(weekStart.getDate() + index)
    return date
  })
}

function formatPreviewWeekLabel(weekDates: Date[]) {
  if (weekDates.length === 0) return ''

  const first = weekDates[0]
  const last = weekDates[weekDates.length - 1]
  const firstMonth = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(first)
  const lastMonth = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(last)
  const firstDay = new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(first)
  const lastDay = new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(last)

  if (first.getFullYear() === last.getFullYear() && first.getMonth() === last.getMonth()) {
    return `${firstMonth} ${firstDay}–${lastDay}`
  }

  return `${firstMonth} ${firstDay}–${lastMonth} ${lastDay}`
}

function isPreviewDateAvailable(
  slots: AvailabilitySlotRecord[],
  date: Date,
  initialPreviewDate: Date | null
) {
  const candidate = new Date(date)
  candidate.setHours(0, 0, 0, 0)
  if (initialPreviewDate) {
    const firstAvailable = new Date(initialPreviewDate)
    firstAvailable.setHours(0, 0, 0, 0)
    if (candidate < firstAvailable) return false
  }

  const activeDays = new Set(slots.map((slot) => slot.dayOfWeek))
  return activeDays.has(candidate.getDay())
}

function getPreviewSlotPills(slots: AvailabilitySlotRecord[], date: Date | null) {
  if (!date) return [] as string[]

  const daySlots = slots
    .filter((slot) => slot.dayOfWeek === date.getDay())
    .sort((left, right) => left.startTime.localeCompare(right.startTime))

  return [...new Set(daySlots.map((slot) => slot.startTime))].slice(0, 6)
}

function areBusinessDraftsEqual(
  left: BusinessProfileDraft | null,
  right: BusinessProfileDraft | null
) {
  if (left === right) return true
  if (!left || !right) return false

  return (
    left.businessName === right.businessName &&
    left.slug === right.slug &&
    left.bio === right.bio &&
    left.phone === right.phone &&
    left.timezone === right.timezone &&
    left.logoUrl === right.logoUrl
  )
}

export function BookingPreviewCard({
  configuration,
}: {
  configuration: OperatorConfigurationState
}) {
  const activeServices = configuration.services.filter((service) => service.isActive)
  const activeServiceIds = useMemo(
    () => activeServices.map((service) => service.id),
    [activeServices]
  )
  const displayServices = activeServices.slice(0, 3)
  const serviceRows = [
    displayServices[0] ?? null,
    displayServices[1] ?? null,
    displayServices[2] ?? null,
  ]
  const operatorName = configuration.operator?.businessName?.trim() || 'Your business name'
  const operatorBio =
    configuration.operator?.bio?.trim() || 'Add a short business description to preview it here.'
  const operatorPhone = configuration.operator?.phone?.trim()
    ? formatPhoneDisplay(configuration.operator.phone)
    : 'Add a phone number'
  const operatorMedia = configuration.operator as
    | (OperatorProfile & {
        avatar?: string | null
        image?: string | null
        logo?: string | null
        logoUrl?: string | null
      })
    | null
  const previewLogoSrc =
    operatorMedia?.logoUrl ?? operatorMedia?.logo ?? operatorMedia?.image ?? operatorMedia?.avatar
  const previewSlug = toPreviewSlug(configuration.operator?.slug?.trim() || operatorName)
  const previewAddress =
    configuration.serviceArea?.centerAddressText?.trim() || 'Add a service area'
  const previewServiceAddress =
    configuration.locations.find((location) => location.addressText?.trim())?.addressText?.trim() ||
    '1458 Market Street, Suite 210'
  const previewMeta = [
    configuration.operator?.phone?.trim() ? operatorPhone : null,
    configuration.serviceArea?.centerAddressText?.trim() ? previewAddress : null,
  ].filter(Boolean) as string[]
  const initialPreviewDate = useMemo(
    () => getInitialPreviewDate(configuration.slots),
    [configuration.slots]
  )
  const initialPreviewWeekStart = useMemo(
    () => startOfPreviewWeek(initialPreviewDate ?? new Date()),
    [initialPreviewDate]
  )
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>(() =>
    displayServices[0]?.id ? [displayServices[0].id] : []
  )
  const [previewStep, setPreviewStep] = useState<'selection' | 'booking'>('selection')
  const [visibleWeekStart, setVisibleWeekStart] = useState(initialPreviewWeekStart)
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialPreviewDate)
  const [selectedSlot, setSelectedSlot] = useState('')
  const selectedServices = useMemo(
    () =>
      displayServices
        .filter((service): service is NonNullable<typeof service> => Boolean(service))
        .filter((service) => selectedServiceIds.includes(service.id)),
    [displayServices, selectedServiceIds]
  )
  const weekDates = useMemo(() => buildPreviewWeekDates(visibleWeekStart), [visibleWeekStart])
  const weekLabel = useMemo(() => formatPreviewWeekLabel(weekDates), [weekDates])
  const canGoToPreviousWeek = visibleWeekStart.getTime() > initialPreviewWeekStart.getTime()
  const selectedDateLabel = useMemo(
    () =>
      selectedDate
        ? new Intl.DateTimeFormat('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          }).format(selectedDate)
        : null,
    [selectedDate]
  )
  const nextOpeningLabel = selectedDateLabel ?? 'Set hours'

  const previewUrl = `https://zippity.app/book/${previewSlug || 'your-slug'}`
  const slotPills = useMemo(
    () => getPreviewSlotPills(configuration.slots, selectedDate),
    [configuration.slots, selectedDate]
  )
  const totalDurationMinutes = selectedServices.reduce(
    (total, service) =>
      total + service.durationMinutes + service.bufferMinutesBefore + service.bufferMinutesAfter,
    0
  )
  const totalSelectedPrice = selectedServices.reduce(
    (total, service) => total + (service.price ?? 0),
    0
  )

  useEffect(() => {
    setSelectedServiceIds((current) => {
      const next = current.filter((id) => activeServiceIds.includes(id))
      if (next.length === current.length && next.every((id, index) => id === current[index])) {
        return current
      }
      return next
    })
  }, [activeServiceIds])

  useEffect(() => {
    if (selectedServices.length === 0 && previewStep === 'booking') {
      setPreviewStep('selection')
    }
  }, [previewStep, selectedServices.length])

  useEffect(() => {
    setVisibleWeekStart(initialPreviewWeekStart)
  }, [initialPreviewWeekStart])

  useEffect(() => {
    const availableWeekDates = weekDates.filter((date) =>
      isPreviewDateAvailable(configuration.slots, date, initialPreviewDate)
    )

    if (availableWeekDates.length === 0) {
      setSelectedDate(null)
      return
    }

    const stillVisible = selectedDate
      ? availableWeekDates.some(
          (date) =>
            date.getFullYear() === selectedDate.getFullYear() &&
            date.getMonth() === selectedDate.getMonth() &&
            date.getDate() === selectedDate.getDate()
        )
      : false

    if (!stillVisible) {
      setSelectedDate(availableWeekDates[0])
    }
  }, [configuration.slots, initialPreviewDate, selectedDate, weekDates])

  useEffect(() => {
    if (slotPills.length === 0) {
      setSelectedSlot('')
      return
    }

    if (!slotPills.includes(selectedSlot)) {
      setSelectedSlot(slotPills[0])
    }
  }, [selectedSlot, slotPills])

  return (
    <PremiumPanel className={cn(setupPanelClass, PREVIEW.card.frame)}>
      <CardHeader className={PREVIEW.browser.bar}>
        <div className={PREVIEW.browser.dots}>
          <span className="h-2 w-2 rounded-full bg-red-400/80" />
          <span className="h-2 w-2 rounded-full bg-amber-400/80" />
          <span className="h-2 w-2 rounded-full bg-emerald-400/80" />
        </div>
        <div className={PREVIEW.browser.address}>{previewUrl}</div>
      </CardHeader>

      <div className={PREVIEW.stage.shell}>
        <header className={PREVIEW.stage.heroRow}>
          <div className={PREVIEW.stage.identityRow}>
            <Avatar className="h-11 w-11 border border-border/60 bg-background shadow-none">
              {previewLogoSrc ? <AvatarImage src={previewLogoSrc} alt={operatorName} /> : null}
              <AvatarFallback className="bg-muted text-xs font-semibold text-foreground">
                {getInitials(operatorName) || <Logo className="h-4 w-4" />}
              </AvatarFallback>
            </Avatar>

            <div className={PREVIEW.stage.titleStack}>
              <CardTitle className="line-clamp-1 text-lg tracking-tight text-foreground md:text-xl">
                {operatorName}
              </CardTitle>
              <CardDescription className="line-clamp-1 max-w-xl text-sm text-muted-foreground">
                {operatorBio}
              </CardDescription>
              {previewMeta.length > 0 ? (
                <div className={PREVIEW.stage.meta}>
                  {previewMeta.map((item, index) => (
                    <span key={item} className="inline-flex items-center gap-3">
                      {index > 0 ? (
                        <span className="h-1 w-1 rounded-full bg-muted-foreground/45" />
                      ) : null}
                      <span className="line-clamp-1">{item}</span>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="shrink-0 text-right">
            <Badge
              variant="outline"
              className="border-border/60 bg-background/84 text-[11px] uppercase tracking-[0.18em] text-muted-foreground shadow-none"
            >
              <CalendarIcon className="mr-1 h-3.5 w-3.5" />
              Next opening
            </Badge>
            <p className="mt-2 text-sm font-semibold text-foreground">{nextOpeningLabel}</p>
          </div>
        </header>

        {previewStep === 'selection' ? (
          <div className={PREVIEW.stage.stack}>
            <Card
              className={cn(
                PREVIEW.card.shell,
                'setup-preview-surface flex min-h-0 flex-1 flex-col'
              )}
            >
              <CardHeader className={FORM.card.headerCompact}>
                <div className={LAYOUT.rows.splitCenter}>
                  <CardTitle className="text-lg text-foreground">Choose services</CardTitle>
                  <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                    Pick one or more
                  </span>
                </div>
                <CardDescription className="text-sm text-muted-foreground">
                  Choose all the services you'd like to book for this visit.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-3 overflow-y-auto pt-0">
                {serviceRows.map((service, index) =>
                  service ? (
                    <Card
                      key={service.id}
                      className={cn(
                        surfaceRadii.xl,
                        PREVIEW.card.selection,
                        selectedServiceIds.includes(service.id)
                          ? 'border-primary/30 bg-background text-foreground'
                          : 'border-border/60 bg-background/74 text-foreground'
                      )}
                    >
                      <CardContent className={PREVIEW.card.selectionBody}>
                        <div className={FORM.fieldStack}>
                          <div className={FORM.rows.tags}>
                            <p className="truncate text-sm font-semibold md:text-base">
                              {service.name}
                            </p>
                            {selectedServiceIds.includes(service.id) ? (
                              <Badge className="border-0 bg-primary text-primary-foreground shadow-none">
                                Selected
                              </Badge>
                            ) : null}
                          </div>
                          <div className={PREVIEW.card.selectionSummary}>
                            <span>{service.durationMinutes} min</span>
                            {service.bufferMinutesBefore + service.bufferMinutesAfter > 0 ? (
                              <span>
                                + {service.bufferMinutesBefore + service.bufferMinutesAfter} min
                                buffer
                              </span>
                            ) : null}
                            {service.price != null ? (
                              <Badge
                                variant="secondary"
                                className={cn(
                                  'border-0 shadow-none',
                                  selectedServiceIds.includes(service.id)
                                    ? 'bg-primary/10 text-primary'
                                    : 'bg-muted text-muted-foreground'
                                )}
                              >
                                {formatCurrency(service.price, service.currency)}
                              </Badge>
                            ) : null}
                          </div>
                          {service.description ? (
                            <p className="line-clamp-2 text-xs md:text-sm text-muted-foreground">
                              {service.description}
                            </p>
                          ) : null}
                        </div>
                        <Button
                          size="sm"
                          variant={selectedServiceIds.includes(service.id) ? 'default' : 'outline'}
                          className={cn(
                            'min-w-20 shrink-0',
                            selectedServiceIds.includes(service.id)
                              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                              : 'border-border/60 bg-transparent text-foreground hover:bg-muted'
                          )}
                          onClick={() => {
                            setSelectedServiceIds((current) =>
                              current.includes(service.id)
                                ? current.filter((id) => id !== service.id)
                                : [...current, service.id]
                            )
                          }}
                        >
                          {selectedServiceIds.includes(service.id) ? 'Remove' : 'Add'}
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card key={`placeholder-service-${index}`} className={PREVIEW.card.placeholder}>
                      <CardContent className="p-4">
                        <div className="h-4 w-40 rounded bg-muted" />
                        <div className="mt-2 h-3 w-28 rounded bg-muted" />
                      </CardContent>
                    </Card>
                  )
                )}
              </CardContent>
            </Card>

            <div className={PREVIEW.footerBar}>
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-muted-foreground">
                  Appointment estimate
                </p>
                <div className={PREVIEW.stage.sectionMeta}>
                  {selectedServices.length > 0 ? (
                    <span>{selectedServices.length} services</span>
                  ) : null}
                  {totalDurationMinutes > 0 ? <span>{totalDurationMinutes} min total</span> : null}
                  {totalSelectedPrice > 0 ? (
                    <span>{formatCurrency(totalSelectedPrice, selectedServices[0]?.currency)}</span>
                  ) : null}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Address: {previewServiceAddress}
                </p>
              </div>
              <Button
                type="button"
                className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={selectedServices.length === 0}
                onClick={() => setPreviewStep('booking')}
              >
                Book Appointment
              </Button>
            </div>
          </div>
        ) : (
          <div className={PREVIEW.stage.stack}>
            <Card className={cn(PREVIEW.card.shell, 'setup-preview-surface flex min-h-0 flex-col')}>
              <CardHeader className={FORM.card.headerCompact}>
                <div className={LAYOUT.rows.split}>
                  <div className={PREVIEW.stage.titleStack}>
                    <CardTitle className="text-lg text-foreground">Book appointment</CardTitle>
                    <CardDescription className="text-sm text-muted-foreground">
                      Choose a day and time that works for your appointment.
                    </CardDescription>
                  </div>
                  <Badge
                    variant="outline"
                    className="max-w-[160px] truncate border-border/60 bg-background/80 text-[11px] uppercase tracking-[0.18em] text-muted-foreground shadow-none"
                  >
                    {selectedServices.length} selected
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className={FORM.card.body}>
                <div className={FORM.rows.badge}>
                  {selectedServices.map((service) => (
                    <Badge
                      key={`selected-service-${service.id}`}
                      variant="secondary"
                      className="border-0 bg-primary/10 text-primary shadow-none"
                    >
                      {service.name}
                    </Badge>
                  ))}
                </div>

                <div className={FORM.fieldStack}>
                  <div className={LAYOUT.rows.splitCenter}>
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                      {weekLabel}
                    </p>
                    <div className={LAYOUT.rows.inlineTight}>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-border/60 bg-background/80"
                        onClick={() => {
                          if (!canGoToPreviousWeek) return
                          const previousWeek = new Date(visibleWeekStart)
                          previousWeek.setDate(visibleWeekStart.getDate() - 7)
                          setVisibleWeekStart(previousWeek)
                        }}
                        disabled={!canGoToPreviousWeek}
                      >
                        <ArrowLeftIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-border/60 bg-background/80"
                        onClick={() => {
                          const nextWeek = new Date(visibleWeekStart)
                          nextWeek.setDate(visibleWeekStart.getDate() + 7)
                          setVisibleWeekStart(nextWeek)
                        }}
                      >
                        <ArrowRightIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className={PREVIEW.calendar.dateGrid}>
                    {weekDates.map((date) => {
                      const isAvailable = isPreviewDateAvailable(
                        configuration.slots,
                        date,
                        initialPreviewDate
                      )
                      const isSelected =
                        selectedDate != null &&
                        selectedDate.getFullYear() === date.getFullYear() &&
                        selectedDate.getMonth() === date.getMonth() &&
                        selectedDate.getDate() === date.getDate()

                      return (
                        <button
                          key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
                          type="button"
                          onClick={() => {
                            if (!isAvailable) return
                            setSelectedDate(date)
                          }}
                          disabled={!isAvailable}
                          className={cn(
                            surfaceRadii.lg,
                            PREVIEW.calendar.dateCell,
                            isSelected
                              ? 'border-primary/25 bg-primary/10 text-foreground'
                              : isAvailable
                                ? 'border-border/60 bg-background/82 text-foreground'
                                : 'border-border/40 bg-muted/20 text-muted-foreground/50 opacity-60'
                          )}
                        >
                          <span className="text-[11px] uppercase tracking-[0.16em]">
                            {new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date)}
                          </span>
                          <span className="text-xl font-semibold tracking-tight">
                            {new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(date)}
                          </span>
                          <span className="text-[11px] uppercase tracking-[0.16em]">
                            {isAvailable
                              ? summarizeDayWindows(
                                  configuration.slots
                                    .filter((slot) => slot.dayOfWeek === date.getDay())
                                    .map((slot) => ({
                                      id: slot.id,
                                      startTime: slot.startTime,
                                      endTime: slot.endTime,
                                    }))
                                )
                              : 'Off'}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {slotPills.length > 0 ? (
                  <div className={PREVIEW.calendar.slotGrid}>
                    {slotPills.map((time) => (
                      <button
                        key={`time-slot-placeholder-${time}`}
                        type="button"
                        onClick={() => setSelectedSlot(time)}
                        className={cn(
                          surfaceRadii.lg,
                          'setup-preview-slot-chip h-11 border text-xs font-medium shadow-none transition-colors duration-200',
                          selectedSlot === time
                            ? 'border-primary/20 bg-primary text-primary-foreground'
                            : 'border-border/60 bg-background/80 text-muted-foreground'
                        )}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className={SURFACE.emptyState}>No times on this day.</div>
                )}
              </CardContent>
            </Card>

            <div className={PREVIEW.footerBar}>
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-muted-foreground">
                  Appointment summary
                </p>
                <div className={PREVIEW.stage.sectionMeta}>
                  {selectedServices.length > 0 ? (
                    <span>{selectedServices.length} services</span>
                  ) : null}
                  {totalDurationMinutes > 0 ? <span>{totalDurationMinutes} min</span> : null}
                  {selectedDateLabel ? <span>{selectedDateLabel}</span> : null}
                  {selectedSlot ? <span>{selectedSlot}</span> : null}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{previewServiceAddress}</p>
              </div>
              <div className={PREVIEW.stage.footerActions}>
                <Button
                  type="button"
                  variant="outline"
                  className="border-border/60 bg-transparent text-foreground hover:bg-muted"
                  onClick={() => setPreviewStep('selection')}
                >
                  Edit services
                </Button>
                <Button
                  size="sm"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled
                >
                  Confirm booking
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PremiumPanel>
  )
}

export function AvailabilityEditor({
  configuration,
  onSaved,
  hidePrimarySubmit = false,
  onRegisterSubmit,
  onPreviewChange,
}: {
  configuration: OperatorConfigurationState
  onSaved?: () => Promise<void> | void
  hidePrimarySubmit?: boolean
  onRegisterSubmit?: (submit: () => Promise<boolean>) => void
  onPreviewChange?: (slots: AvailabilitySlotRecord[]) => void
}) {
  const setScheduleMutation = trpc.schedule.set.useMutation()

  const [windows, setWindows] = useState<WeeklyWindow[][]>(() =>
    buildInitialAvailabilityWindows(configuration.slots)
  )
  const [dayEnabled, setDayEnabled] = useState<boolean[]>(() =>
    buildInitialDayEnabled(configuration.slots)
  )
  const [showWeekends, setShowWeekends] = useState(() => {
    const initialWindows = buildInitialAvailabilityWindows(configuration.slots)
    return initialWindows[0].length > 0 || initialWindows[6].length > 0
  })
  const [isMobileLayout, setIsMobileLayout] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  )
  const [expandedMobileDay, setExpandedMobileDay] = useState<number | null>(1)
  const [dragSelection, setDragSelection] = useState<{
    dayOfWeek: number
    startSlot: number
    endSlot: number
  } | null>(null)
  const timelineScrollRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const timelineScrollInitializedRef = useRef<Record<number, boolean>>({})

  useEffect(() => {
    const nextWindows = buildInitialAvailabilityWindows(configuration.slots)
    setWindows(nextWindows)
    setDayEnabled(buildInitialDayEnabled(configuration.slots))
    setShowWeekends(nextWindows[0].length > 0 || nextWindows[6].length > 0)
  }, [configuration.slots])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const handleChange = (event: MediaQueryListEvent) => setIsMobileLayout(event.matches)

    setIsMobileLayout(mediaQuery.matches)
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const isDayActive = useCallback(
    (dayOfWeek: number) => {
      if ((dayOfWeek === 0 || dayOfWeek === 6) && !showWeekends) {
        return false
      }
      return dayEnabled[dayOfWeek] ?? false
    },
    [dayEnabled, showWeekends]
  )

  const effectiveWindows = useMemo(
    () => windows.map((dayWindows, dayOfWeek) => (isDayActive(dayOfWeek) ? dayWindows : [])),
    [isDayActive, windows]
  )

  const commitDragSelection = useCallback(() => {
    if (!dragSelection) return

    const dayOfWeek = dragSelection.dayOfWeek
    const startSlot = Math.min(dragSelection.startSlot, dragSelection.endSlot)
    const endSlot = Math.max(dragSelection.startSlot, dragSelection.endSlot) + 1

    const nextWindow: WeeklyWindow = {
      id: crypto.randomUUID(),
      startTime: timelineSlotIndexToTime(startSlot),
      endTime: timelineSlotIndexToTime(endSlot),
    }

    setDayEnabled((current) =>
      current.map((enabled, dayIndex) => (dayIndex === dayOfWeek ? true : enabled))
    )
    setWindows((current) =>
      current.map((dayWindows, dayIndex) =>
        dayIndex === dayOfWeek ? normalizeDayWindows([...dayWindows, nextWindow]) : dayWindows
      )
    )
    setDragSelection(null)
  }, [dragSelection])

  useEffect(() => {
    if (!dragSelection) return

    function handleMouseUp() {
      commitDragSelection()
    }

    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [commitDragSelection, dragSelection])

  const handleSaveSchedule = useCallback(async () => {
    const totalWindowCount = effectiveWindows.reduce(
      (total, dayWindows) => total + dayWindows.length,
      0
    )
    if (totalWindowCount === 0) {
      toast.error('Add at least one weekly window before continuing.')
      return false
    }

    const hasInvalidWindow = effectiveWindows.some((dayWindows) =>
      dayWindows.some((window) => minutesBetween(window.startTime, window.endTime) <= 0)
    )
    if (hasInvalidWindow) {
      toast.error('Every hours window needs an end time after its start time.')
      return false
    }

    try {
      await setScheduleMutation.mutateAsync({
        scheduleId: configuration.schedule?.id,
        timezone: configuration.operator?.timezone,
        slots: effectiveWindows.flatMap((dayWindows, dayOfWeek) =>
          dayWindows.map((window) => ({
            dayOfWeek,
            startTime: window.startTime,
            endTime: window.endTime,
            locationId: null,
          }))
        ),
      })
      await onSaved?.()
      return true
    } catch (error) {
      toast.error('Unable to save hours.', {
        description: error instanceof Error ? error.message : 'Try again in a moment.',
      })
      return false
    }
  }, [
    configuration.operator?.timezone,
    configuration.schedule?.id,
    effectiveWindows,
    onSaved,
    setScheduleMutation,
  ])

  function removeWindow(dayOfWeek: number, windowId: string) {
    setWindows((current) =>
      current.map((dayWindows, dayIndex) => {
        if (dayIndex !== dayOfWeek) return dayWindows
        return dayWindows.filter((window) => window.id !== windowId)
      })
    )
  }

  function setDayEnabledState(dayOfWeek: number, enabled: boolean) {
    setDayEnabled((current) =>
      current.map((value, dayIndex) => (dayIndex === dayOfWeek ? enabled : value))
    )
  }

  function copyDayToAll(dayOfWeek: number) {
    const sourceWindows = windows[dayOfWeek] ?? []
    const sourceEnabled = isDayActive(dayOfWeek)
    const targetDayIndices = showWeekends ? [1, 2, 3, 4, 5, 6, 0] : [1, 2, 3, 4, 5]

    setWindows((current) =>
      current.map((dayWindows, index) => {
        if (!targetDayIndices.includes(index)) return dayWindows
        return sourceEnabled
          ? sourceWindows.map((window) => ({ ...window, id: `${index}-${crypto.randomUUID()}` }))
          : []
      })
    )
    setDayEnabled((current) =>
      current.map((enabled, index) => (targetDayIndices.includes(index) ? sourceEnabled : enabled))
    )
  }

  function resetDay(dayOfWeek: number) {
    setWindows((current) =>
      current.map((dayWindows, dayIndex) => (dayIndex === dayOfWeek ? [] : dayWindows))
    )
  }

  function toggleWeekends(enabled: boolean) {
    setShowWeekends(enabled)
    if (enabled) {
      setDayEnabled((current) => {
        const next = [...current]
        next[0] = next[0] || (windows[0]?.length ?? 0) > 0
        next[6] = next[6] || (windows[6]?.length ?? 0) > 0
        return next
      })
    }
  }

  function applyWeekdayPreset() {
    setDayEnabled((current) =>
      current.map((enabled, dayOfWeek) => (dayOfWeek >= 1 && dayOfWeek <= 5 ? true : enabled))
    )
    setWindows((current) =>
      current.map((dayWindows, dayOfWeek) => {
        if (dayOfWeek < 1 || dayOfWeek > 5) return dayWindows
        return [createWeeklyWindow(dayOfWeek, '09:00', '17:00')]
      })
    )
  }

  function toggleExpandedMobileDay(dayOfWeek: number) {
    setExpandedMobileDay((current) => (current === dayOfWeek ? null : dayOfWeek))
  }

  function startTimelineDrag(dayOfWeek: number, slotIndex: number) {
    if (!isDayActive(dayOfWeek)) return
    setDragSelection({ dayOfWeek, startSlot: slotIndex, endSlot: slotIndex })
  }

  function updateTimelineDrag(dayOfWeek: number, slotIndex: number) {
    setDragSelection((current) => {
      if (!current || current.dayOfWeek !== dayOfWeek) return current
      return { ...current, endSlot: slotIndex }
    })
  }

  function handleTimelineAutoScroll(dayOfWeek: number, event: { clientX: number }) {
    if (!dragSelection || dragSelection.dayOfWeek !== dayOfWeek) return

    const container = timelineScrollRefs.current[dayOfWeek]
    if (!container) return

    const rect = container.getBoundingClientRect()
    const threshold = 64
    const step = 28

    if (event.clientX < rect.left + threshold) {
      container.scrollLeft = Math.max(0, container.scrollLeft - step)
      return
    }

    if (event.clientX > rect.right - threshold) {
      container.scrollLeft = Math.min(
        container.scrollWidth - container.clientWidth,
        container.scrollLeft + step
      )
    }
  }

  useEffect(() => {
    onRegisterSubmit?.(handleSaveSchedule)
  }, [onRegisterSubmit, handleSaveSchedule])

  useEffect(() => {
    const previewSlots = effectiveWindows.flatMap((dayWindows, dayOfWeek) =>
      dayWindows.map((window) => ({
        id: window.id,
        dayOfWeek,
        startTime: window.startTime,
        endTime: window.endTime,
        locationId: null,
      }))
    )
    onPreviewChange?.(previewSlots)
  }, [effectiveWindows, onPreviewChange])

  const weeklyHoursLabel = useMemo(() => {
    const totalMinutes = effectiveWindows.reduce(
      (total, dayWindows) =>
        total +
        dayWindows.reduce(
          (dayTotal, window) =>
            dayTotal + Math.max(0, minutesBetween(window.startTime, window.endTime)),
          0
        ),
      0
    )

    if (totalMinutes <= 0) return 'No hours yet'
    const hours = totalMinutes / 60
    return `${hours % 1 === 0 ? hours.toFixed(0) : hours.toFixed(1)} hr / week`
  }, [effectiveWindows])

  const visibleDayIndices = showWeekends ? [1, 2, 3, 4, 5, 6, 0] : [1, 2, 3, 4, 5]
  const compactWeekendMode = showWeekends && visibleDayIndices.length > 5

  useEffect(() => {
    if (!isMobileLayout) return

    setExpandedMobileDay((current) => {
      if (current === null) {
        return null
      }

      if (visibleDayIndices.includes(current)) {
        return current
      }

      const firstActiveVisibleDay = visibleDayIndices.find((dayOfWeek) => isDayActive(dayOfWeek))
      return firstActiveVisibleDay ?? visibleDayIndices[0] ?? 1
    })
  }, [isDayActive, isMobileLayout, visibleDayIndices])

  useEffect(() => {
    visibleDayIndices.forEach((dayOfWeek) => {
      const container = timelineScrollRefs.current[dayOfWeek]
      if (!container || timelineScrollInitializedRef.current[dayOfWeek]) return

      const maxScroll = container.scrollWidth - container.clientWidth
      const defaultOffset =
        ((7.5 - TIMELINE_START_HOUR) / (TIMELINE_END_HOUR - TIMELINE_START_HOUR)) *
        container.scrollWidth
      container.scrollLeft = Math.min(maxScroll, Math.max(0, defaultOffset))
      timelineScrollInitializedRef.current[dayOfWeek] = true
    })
  }, [visibleDayIndices])

  return (
    <div className={cn(stepStackClass, 'flex min-h-0 flex-1 flex-col')}>
      <section
        className={cn(
          SURFACE.editorSection,
          'flex min-h-0 flex-1 flex-col',
          compactWeekendMode ? 'space-y-3' : 'space-y-5'
        )}
      >
        <div
          className={cn(
            FORM.rows.headerUtility,
            'items-center gap-3',
            compactWeekendMode ? 'sm:gap-3' : 'sm:gap-4'
          )}
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {configuration.operator?.timezone || 'Timezone pending'}
              </span>
              <span aria-hidden="true">•</span>
              <span>{weeklyHoursLabel}</span>
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-between gap-2 rounded-[4px] border border-border/55 bg-background/80 px-2 py-2">
            <span className="text-sm font-medium text-foreground">Show weekends</span>
            <Switch
              checked={showWeekends}
              onCheckedChange={toggleWeekends}
              aria-label="Show weekend hours"
              className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted"
            />
          </div>
        </div>

        {weeklyHoursLabel === 'No hours yet' ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-y border-border/45 py-3 text-sm text-muted-foreground">
            <span>Drag across a day to add hours, or start with a weekday schedule.</span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-8 rounded-[4px] px-3"
              onClick={applyWeekdayPreset}
            >
              Weekdays 9–5
            </Button>
          </div>
        ) : null}

        <div
          className={cn(
            TIMELINE.list,
            compactWeekendMode ? 'min-h-0 flex-1 overflow-y-auto pr-1 scrollbar-hide' : ''
          )}
        >
          {visibleDayIndices.map((dayOfWeek) => {
            const dayName = DAYS[dayOfWeek]
            const dayWindows = windows[dayOfWeek] ?? []
            const dayActive = isDayActive(dayOfWeek)
            const draftStyle =
              dragSelection && dragSelection.dayOfWeek === dayOfWeek
                ? getDraftTimelineStyle(dragSelection.startSlot, dragSelection.endSlot)
                : null
            const daySummary =
              dayActive && dayWindows.length > 0 ? summarizeDayWindows(dayWindows) : null
            const isExpandedMobileDay = !isMobileLayout || expandedMobileDay === dayOfWeek
            const mobileExpandedCard = isMobileLayout && isExpandedMobileDay
            const mobileDayToggle = (
              <div className="flex min-h-11 items-center gap-2 rounded-[4px] border border-border/70 bg-background/92 px-2.5 shadow-[var(--app-shadow-soft)]">
                <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                  {dayActive ? 'On' : 'Off'}
                </span>
                <Switch
                  checked={dayActive}
                  onCheckedChange={(checked) => setDayEnabledState(dayOfWeek, checked)}
                  aria-label={`Enable ${dayName}`}
                  className="scale-95 touch-manipulation data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted"
                />
              </div>
            )
            const mobileExpandButton = (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  'h-10 touch-manipulation rounded-[4px] px-3 font-medium text-muted-foreground',
                  mobileExpandedCard ? 'w-10 px-0' : 'text-xs'
                )}
                onClick={() => toggleExpandedMobileDay(dayOfWeek)}
                aria-label={
                  isExpandedMobileDay ? `Collapse ${dayName} editor` : `Edit ${dayName} hours`
                }
              >
                {mobileExpandedCard ? null : 'Edit'}
                <ChevronDownIcon
                  className={cn(
                    'h-4 w-4 transition-transform',
                    isExpandedMobileDay ? 'rotate-180' : ''
                  )}
                />
              </Button>
            )
            const mobileExpandedToolbar = (
              <div className="flex items-center justify-end gap-1.5">
                {mobileDayToggle}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-11 w-11 touch-manipulation rounded-[4px] px-0 text-muted-foreground/75 hover:text-foreground"
                  aria-label={`Copy ${dayName} to visible days`}
                  title="Copy to visible days"
                  onClick={() => copyDayToAll(dayOfWeek)}
                >
                  <CopyIcon className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-11 w-11 touch-manipulation rounded-[4px] px-0 text-muted-foreground/75 hover:text-foreground"
                  aria-label={`Reset ${dayName}`}
                  title="Reset day"
                  onClick={() => resetDay(dayOfWeek)}
                >
                  <ResetIcon className="h-4 w-4" />
                </Button>
                {mobileExpandButton}
              </div>
            )

            return (
              <div
                key={dayName}
                className={cn(
                  TIMELINE.dayCard,
                  compactWeekendMode ? 'gap-2 px-2 py-2' : '',
                  mobileExpandedCard ? 'gap-3 px-2 py-2' : '',
                  dayActive ? 'opacity-100' : 'bg-muted/10 opacity-55'
                )}
              >
                <div
                  className={cn(
                    TIMELINE.headerStack,
                    isMobileLayout ? 'gap-2' : '',
                    mobileExpandedCard ? 'gap-2' : ''
                  )}
                >
                  <div
                    className={cn(
                      isMobileLayout
                        ? 'flex items-start justify-between gap-3'
                        : 'flex items-center justify-between gap-3'
                    )}
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="text-sm font-semibold text-foreground">
                        {DAY_SHORT_LABELS[dayOfWeek]}
                      </div>
                      {daySummary ? (
                        <div className="text-xs text-muted-foreground">{daySummary}</div>
                      ) : isMobileLayout ? (
                        <div className="text-xs text-muted-foreground">
                          {dayActive ? 'Tap to edit hours' : 'Turn on to set hours'}
                        </div>
                      ) : null}
                    </div>

                    {isMobileLayout
                      ? mobileExpandedCard
                        ? mobileExpandedToolbar
                        : mobileExpandButton
                      : null}
                  </div>

                  {!isMobileLayout ? (
                    <div className={TIMELINE.headerRow}>
                      <Switch
                        checked={dayActive}
                        onCheckedChange={(checked) => setDayEnabledState(dayOfWeek, checked)}
                        className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted"
                      />
                      <div className={TIMELINE.actionRow}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={TIMELINE.iconButton}
                          aria-label={`Copy ${dayName} to visible days`}
                          title="Copy to visible days"
                          onClick={() => copyDayToAll(dayOfWeek)}
                        >
                          <CopyIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={TIMELINE.iconButton}
                          aria-label={`Reset ${dayName}`}
                          title="Reset day"
                          onClick={() => resetDay(dayOfWeek)}
                        >
                          <ResetIcon className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ) : isExpandedMobileDay ? null : (
                    <div className="flex items-center justify-start">{mobileDayToggle}</div>
                  )}
                </div>

                <div
                  className={cn(
                    TIMELINE.body,
                    dayActive ? 'opacity-100' : 'pointer-events-none opacity-35',
                    isExpandedMobileDay ? '' : 'hidden',
                    mobileExpandedCard ? 'pt-1' : ''
                  )}
                >
                  <div
                    ref={(node) => {
                      timelineScrollRefs.current[dayOfWeek] = node
                    }}
                    style={TIMELINE_WIDTH_STYLE}
                    role="application"
                    onMouseMove={(event) => handleTimelineAutoScroll(dayOfWeek, event)}
                    className={cn(
                      TIMELINE.viewport,
                      compactWeekendMode ? 'p-1' : '',
                      mobileExpandedCard ? 'px-1.5 py-1.5' : ''
                    )}
                  >
                    <div
                      className={cn(
                        TIMELINE.canvas,
                        compactWeekendMode ? 'h-[2.75rem]' : '',
                        mobileExpandedCard ? 'h-[4.75rem]' : ''
                      )}
                    >
                      <div className={TIMELINE.hourRail}>
                        {Array.from(
                          { length: TIMELINE_END_HOUR - TIMELINE_START_HOUR + 1 },
                          (_, hourIndex) => {
                            if (
                              hourIndex !== 0 &&
                              hourIndex !== TIMELINE_END_HOUR - TIMELINE_START_HOUR &&
                              hourIndex % 2 !== 0
                            ) {
                              return null
                            }

                            const left =
                              (hourIndex / (TIMELINE_END_HOUR - TIMELINE_START_HOUR)) * 100
                            return (
                              <span
                                key={`${dayName}-hour-${hourIndex}`}
                                className={cn(
                                  TIMELINE.hourLabel,
                                  hourIndex === 0
                                    ? 'translate-x-0'
                                    : hourIndex === TIMELINE_END_HOUR - TIMELINE_START_HOUR
                                      ? '-translate-x-full'
                                      : '-translate-x-1/2'
                                )}
                                style={{ left: `${left}%` }}
                              >
                                {formatTimelineHourLabel(hourIndex)}
                              </span>
                            )
                          }
                        )}
                      </div>

                      <div
                        className={cn(TIMELINE.slotLayer, mobileExpandedCard ? 'h-[3.125rem]' : '')}
                      >
                        {Array.from({ length: TIMELINE_SLOT_COUNT }, (_, slotIndex) => (
                          <button
                            key={`${dayName}-slot-${slotIndex}`}
                            type="button"
                            disabled={!dayActive}
                            aria-label={`Drag from ${formatCompactTimeLabel(timelineSlotIndexToTime(slotIndex))} to add ${dayName} hours`}
                            title={`Drag from ${formatCompactTimeLabel(timelineSlotIndexToTime(slotIndex))} to add hours`}
                            className={cn(
                              TIMELINE.slotButton,
                              slotIndex % 2 === 0 ? TIMELINE.slotStripe : 'bg-transparent',
                              dayActive ? TIMELINE.slotHover : ''
                            )}
                            style={{
                              left: `${(slotIndex / TIMELINE_SLOT_COUNT) * 100}%`,
                              width: `${(1 / TIMELINE_SLOT_COUNT) * 100}%`,
                            }}
                            onMouseDown={() => startTimelineDrag(dayOfWeek, slotIndex)}
                            onMouseEnter={() => updateTimelineDrag(dayOfWeek, slotIndex)}
                            onMouseUp={commitDragSelection}
                          />
                        ))}

                        {dayWindows.map((window) => {
                          const style = getTimelineWindowStyle(window)
                          return (
                            <button
                              key={window.id}
                              type="button"
                              title={`${formatCompactTimeLabel(window.startTime)}–${formatCompactTimeLabel(window.endTime)}`}
                              className={TIMELINE.window}
                              style={style}
                              onClick={() => removeWindow(dayOfWeek, window.id)}
                            >
                              <span className="line-clamp-2 block">
                                {formatCompactTimeLabel(window.startTime)}–
                                {formatCompactTimeLabel(window.endTime)}
                              </span>
                            </button>
                          )
                        })}

                        {draftStyle ? (
                          <div className={TIMELINE.draftWindow} style={draftStyle} />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {!hidePrimarySubmit ? (
          <Button
            onClick={() => void handleSaveSchedule()}
            disabled={setScheduleMutation.isPending}
          >
            {setScheduleMutation.isPending ? 'Saving…' : 'Continue'}
          </Button>
        ) : null}
      </section>
    </div>
  )
}

export function ServicesPricingEditor({
  configuration,
  onSaved,
  onRegisterSubmit,
  onPreviewServicesChange,
}: {
  configuration: OperatorConfigurationState
  onSaved?: () => Promise<void> | void
  onRegisterSubmit?: (submit: () => Promise<boolean>) => void
  onPreviewServicesChange?: (services: ServiceRecord[]) => void
}) {
  const catalogsQuery = trpc.services.catalogs.list.useQuery()
  const activeCatalogId = useMemo(
    () =>
      catalogsQuery.data?.find((catalog) => catalog.isActive)?.id ??
      catalogsQuery.data?.[0]?.id ??
      '',
    [catalogsQuery.data]
  )
  const catalogQuery = trpc.services.catalogs.get.useQuery(
    { catalogId: activeCatalogId },
    { enabled: Boolean(activeCatalogId) }
  )
  const saveCatalogMutation = trpc.services.catalogs.save.useMutation()

  const [catalogName, setCatalogName] = useState('Starter services')
  const [drafts, setDrafts] = useState<OnboardingServiceDraft[]>(() =>
    configuration.services.length > 0
      ? configuration.services.map((service) =>
          createOnboardingServiceDraft({
            id: service.id,
            name: service.name,
            slug: service.slug,
            description: service.description ?? '',
            durationMinutes: service.durationMinutes,
            price: service.price,
            bufferMinutesBefore: service.bufferMinutesBefore,
            bufferMinutesAfter: service.bufferMinutesAfter,
            minimumNoticeMinutes: service.minimumNoticeMinutes,
            allowQuantity: service.allowQuantity,
            quantityLabel: service.quantityLabel ?? 'Quantity',
            maxQuantity: service.maxQuantity ?? 1,
            durationPerAdditionalUnit: service.durationPerAdditionalUnit ?? 0,
            pricePerAdditionalUnit: service.pricePerAdditionalUnit ?? 0,
            isActive: service.isActive,
          })
        )
      : createStarterServiceDrafts()
  )
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(() => new Set())
  const [pendingDeleteRowKey, setPendingDeleteRowKey] = useState<string | null>(null)

  useEffect(() => {
    if (catalogQuery.data) {
      setCatalogName(catalogQuery.data.catalog.name || 'Starter services')
      const nextDrafts = createServiceDraftsFromCatalog(catalogQuery.data)
      setDrafts(nextDrafts.length > 0 ? nextDrafts : createStarterServiceDrafts())
      return
    }

    if (
      catalogsQuery.data &&
      catalogsQuery.data.length === 0 &&
      configuration.services.length === 0
    ) {
      setCatalogName('Starter services')
      setDrafts(createStarterServiceDrafts())
    }
  }, [catalogQuery.data, catalogsQuery.data, configuration.services.length])

  const readiness = useMemo(() => getServiceDraftReadiness(drafts), [drafts])

  useEffect(() => {
    onPreviewServicesChange?.(createPreviewServiceRecords(drafts))
  }, [drafts, onPreviewServicesChange])

  function updateDraft(index: number, patch: Partial<OnboardingServiceDraft>) {
    setDrafts((current) =>
      current.map((draft, draftIndex) => (draftIndex === index ? { ...draft, ...patch } : draft))
    )
  }

  function updateDraftName(index: number, name: string) {
    setDrafts((current) =>
      current.map((draft, draftIndex) => {
        if (draftIndex !== index) return draft
        return {
          ...draft,
          name,
          slug: draft.id ? draft.slug : slugifyServiceValue(name),
        }
      })
    )
  }

  function addService() {
    const nextIndex = drafts.length
    const serviceName = `Service ${nextIndex + 1}`
    setDrafts((current) => [
      ...current,
      createOnboardingServiceDraft({
        name: serviceName,
        slug: slugifyServiceValue(serviceName),
        price: STARTER_STANDARD_VISIT_PRICE_CENTS,
      }),
    ])
    setExpandedRowIds(new Set([`draft-${nextIndex}`]))
  }

  function removeService(index: number) {
    setDrafts((current) => {
      if (current.length === 1) {
        return [createOnboardingServiceDraft({ name: '' })]
      }
      return current.filter((_, draftIndex) => draftIndex !== index)
    })
    setPendingDeleteRowKey(null)
  }

  function toggleExpandedRow(rowKey: string) {
    setExpandedRowIds((current) => (current.has(rowKey) ? new Set() : new Set([rowKey])))
  }

  function expandRow(rowKey: string) {
    setExpandedRowIds(new Set([rowKey]))
  }

  const handleSaveServices = useCallback(async () => {
    const nextReadiness = getServiceDraftReadiness(drafts)
    if (!nextReadiness.canSave) {
      toast.error(nextReadiness.firstBlockingReason ?? 'Add at least one service.')
      return false
    }

    try {
      const payload = buildOnboardingCatalogInput({
        catalogId: activeCatalogId || undefined,
        catalogName,
        drafts,
      })
      await saveCatalogMutation.mutateAsync(
        payload as Parameters<typeof saveCatalogMutation.mutateAsync>[0]
      )
      await catalogsQuery.refetch()
      await onSaved?.()
      toast.success('Services saved')
      return true
    } catch (error) {
      toast.error('Unable to save services.', {
        description: error instanceof Error ? error.message : 'Try again in a moment.',
      })
      return false
    }
  }, [activeCatalogId, catalogName, catalogsQuery, drafts, onSaved, saveCatalogMutation])

  useEffect(() => {
    onRegisterSubmit?.(handleSaveServices)
  }, [handleSaveServices, onRegisterSubmit])

  if (catalogsQuery.isLoading || (activeCatalogId && catalogQuery.isLoading)) {
    return (
      <PremiumPanel className={setupPanelClass}>
        <CardContent className={`${surfaceSpacing.insetY.roomy} text-sm text-muted-foreground`}>
          Loading services…
        </CardContent>
      </PremiumPanel>
    )
  }

  return (
    <PremiumPanel className={setupPanelClass}>
      <CardContent className={cardSectionStackClass}>
        <Card className={cn(insetPanelClass, 'shadow-none')}>
          <CardContent className={cn('space-y-4', FORM.card.contentReset)}>
            <div className="grid items-end gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
              <div className={FORM.fieldStack}>
                <Label htmlFor="onboarding-catalog-name">Menu name</Label>
                <Input
                  id="onboarding-catalog-name"
                  className={cn(FORM.input, 'h-10 rounded-lg')}
                  value={catalogName}
                  onChange={(event) => setCatalogName(event.target.value)}
                  placeholder="Main menu"
                />
              </div>
              <div className="flex justify-start md:justify-end">
                <Button type="button" variant="outline" className="h-10" onClick={addService}>
                  + Service
                </Button>
              </div>
            </div>

            <div
              className={cn(
                'flex flex-wrap items-center justify-between gap-3 border-y py-3 text-sm',
                readiness.canSave
                  ? 'border-border/45 text-muted-foreground'
                  : 'border-primary/45 text-foreground'
              )}
            >
              <span>
                {readiness.canSave
                  ? `${readiness.validActiveServiceCount} service${readiness.validActiveServiceCount === 1 ? '' : 's'} ready.`
                  : readiness.firstBlockingReason}
              </span>
              <span className="text-xs text-muted-foreground">Start with one. Add more later.</span>
            </div>

            <div className={FORM.fieldStack}>
              {drafts.map((draft, index) => {
                const draftKey = draft.id ?? `draft-${index}`
                const rowKey = draft.id ?? `draft-${index}`
                const rowOpen = expandedRowIds.has(rowKey)
                return (
                  <Card
                    key={draftKey}
                    className={cn(
                      'gap-0 py-0 shadow-none transition-colors',
                      rowOpen
                        ? cn(surfaceRadii.lg, 'border-primary/30 bg-primary/[0.025]')
                        : 'rounded-none border-x-0 border-t-0 border-border/45 bg-transparent hover:bg-muted/15'
                    )}
                  >
                    <CardContent className="space-y-2 p-2 md:p-2.5">
                      <div
                        className="grid cursor-pointer items-center gap-2 rounded-lg transition-colors lg:grid-cols-[auto_minmax(0,1fr)_7rem_7rem_auto]"
                        onPointerDown={(event) => {
                          const target = event.target as HTMLElement
                          if (target.closest('[data-no-row-expand]')) return
                          if (target.closest('button,input,label,textarea,select')) return
                          toggleExpandedRow(rowKey)
                        }}
                        onFocusCapture={(event) => {
                          const target = event.target as HTMLElement
                          if (target.closest('[data-no-row-expand]')) return
                          expandRow(rowKey)
                        }}
                      >
                        <label
                          data-no-row-expand
                          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-[4px] border border-border/70 bg-background"
                          title={draft.isActive ? 'Active' : 'Inactive'}
                        >
                          <input
                            type="checkbox"
                            checked={draft.isActive}
                            onChange={(event) =>
                              updateDraft(index, { isActive: event.target.checked })
                            }
                            aria-label={draft.isActive ? 'Active service' : 'Inactive service'}
                            className="h-4 w-4 accent-primary"
                          />
                        </label>

                        <div className={FORM.fieldStack}>
                          <Label htmlFor={`service-name-${index}`} className="sr-only">
                            Service name
                          </Label>
                          <Input
                            id={`service-name-${index}`}
                            value={draft.name}
                            className={cn(FORM.input, 'h-9 rounded-lg text-sm')}
                            onFocus={() => expandRow(rowKey)}
                            onChange={(event) => {
                              expandRow(rowKey)
                              updateDraftName(index, event.target.value)
                            }}
                            placeholder="Standard Visit"
                          />
                        </div>

                        <div className={FORM.fieldStack}>
                          <Label htmlFor={`service-duration-${index}`} className="sr-only">
                            Visit length
                          </Label>
                          <ServiceUnitInput
                            id={`service-duration-${index}`}
                            type="number"
                            min={5}
                            value={draft.durationMinutes}
                            suffix="min"
                            onChange={(value) => {
                              expandRow(rowKey)
                              updateDraft(index, {
                                durationMinutes: Number(value) || 5,
                              })
                            }}
                            ariaLabel="Visit length in minutes"
                          />
                        </div>

                        <div className={FORM.fieldStack}>
                          <Label htmlFor={`service-price-${index}`} className="sr-only">
                            Price
                          </Label>
                          <ServiceUnitInput
                            id={`service-price-${index}`}
                            inputMode="decimal"
                            value={formatCentsForInput(draft.price)}
                            prefix="$"
                            onChange={(value) => {
                              expandRow(rowKey)
                              updateDraft(index, {
                                price: parseCurrencyToCents(value),
                              })
                            }}
                            placeholder="0.00"
                            ariaLabel="Price"
                          />
                        </div>

                        <div className="flex items-center justify-end gap-1">
                          <Button
                            type="button"
                            data-no-row-expand
                            variant="ghost"
                            size="sm"
                            className={cn(
                              'relative h-8 w-[4.75rem] cursor-pointer justify-start bg-transparent px-1 pr-6 text-left text-xs font-medium text-muted-foreground shadow-none transition-colors hover:bg-transparent hover:text-foreground',
                              rowOpen ? 'text-primary hover:text-primary' : ''
                            )}
                            onClick={(event) => {
                              event.stopPropagation()
                              toggleExpandedRow(rowKey)
                            }}
                            aria-label={
                              rowOpen ? 'Collapse service details' : 'Expand service details'
                            }
                          >
                            <span>{rowOpen ? 'Details' : 'Edit'}</span>
                            <ChevronDownIcon
                              className={cn(
                                'absolute right-2 h-3.5 w-3.5 transition-transform',
                                rowOpen ? 'rotate-180' : ''
                              )}
                            />
                          </Button>
                          {pendingDeleteRowKey === rowKey ? (
                            <>
                              <Button
                                type="button"
                                data-no-row-expand
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 cursor-pointer text-muted-foreground hover:bg-muted hover:text-foreground"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  setPendingDeleteRowKey(null)
                                }}
                                aria-label="Cancel delete"
                              >
                                <Cross2Icon className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                data-no-row-expand
                                variant="ghost"
                                size="sm"
                                className="h-8 cursor-pointer px-2 text-xs font-semibold text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  removeService(index)
                                }}
                              >
                                Delete
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              data-no-row-expand
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 cursor-pointer text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={(event) => {
                                event.stopPropagation()
                                setPendingDeleteRowKey(rowKey)
                              }}
                              aria-label="Remove service"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {rowOpen ? (
                        <div className="space-y-2 border-t border-border/60 pt-2">
                          <div className={FORM.fieldStack}>
                            <Label htmlFor={`service-description-${index}`}>Description</Label>
                            <Textarea
                              id={`service-description-${index}`}
                              rows={1}
                              value={draft.description}
                              className={cn(FORM.textarea, 'min-h-12 rounded-lg py-2 text-sm')}
                              onChange={(event) =>
                                updateDraft(index, { description: event.target.value })
                              }
                              placeholder="What customers should know before booking."
                            />
                          </div>

                          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                            <div className={FORM.fieldStack}>
                              <Label htmlFor={`service-slug-${index}`}>Booking URL label</Label>
                              <Input
                                id={`service-slug-${index}`}
                                value={draft.slug}
                                className={cn(FORM.input, 'h-9 rounded-lg text-sm')}
                                onChange={(event) =>
                                  updateDraft(index, {
                                    slug: slugifyServiceValue(event.target.value),
                                  })
                                }
                              />
                            </div>
                            <div className={FORM.fieldStack}>
                              <Label htmlFor={`service-notice-${index}`}>Lead time</Label>
                              <ServiceUnitInput
                                id={`service-notice-${index}`}
                                type="number"
                                min={0}
                                value={draft.minimumNoticeMinutes}
                                suffix="min"
                                onChange={(value) =>
                                  updateDraft(index, {
                                    minimumNoticeMinutes: Number(value) || 0,
                                  })
                                }
                              />
                            </div>
                            <div className={FORM.fieldStack}>
                              <Label htmlFor={`service-buffer-before-${index}`}>
                                Travel buffer before
                              </Label>
                              <ServiceUnitInput
                                id={`service-buffer-before-${index}`}
                                type="number"
                                min={0}
                                value={draft.bufferMinutesBefore}
                                suffix="min"
                                onChange={(value) =>
                                  updateDraft(index, {
                                    bufferMode: 'custom',
                                    bufferMinutesBefore: Number(value) || 0,
                                  })
                                }
                              />
                            </div>
                            <div className={FORM.fieldStack}>
                              <Label htmlFor={`service-buffer-after-${index}`}>
                                Travel buffer after
                              </Label>
                              <ServiceUnitInput
                                id={`service-buffer-after-${index}`}
                                type="number"
                                min={0}
                                value={draft.bufferMinutesAfter}
                                suffix="min"
                                onChange={(value) =>
                                  updateDraft(index, {
                                    bufferMode: 'custom',
                                    bufferMinutesAfter: Number(value) || 0,
                                  })
                                }
                              />
                            </div>
                          </div>

                          <div className="flex h-9 items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-2.5">
                            <Switch
                              checked={draft.allowQuantity}
                              onCheckedChange={(checked) =>
                                updateDraft(index, { allowQuantity: checked })
                              }
                              className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted"
                            />
                            <span className="text-sm font-medium text-foreground">
                              Let customers choose a quantity
                            </span>
                          </div>

                          {draft.allowQuantity ? (
                            <div className="grid gap-2 border-l-2 border-primary/20 pl-2 md:grid-cols-2 xl:grid-cols-4">
                              <div className={FORM.fieldStack}>
                                <Label htmlFor={`service-quantity-label-${index}`}>
                                  Quantity name
                                </Label>
                                <Input
                                  id={`service-quantity-label-${index}`}
                                  value={draft.quantityLabel}
                                  className={cn(FORM.input, 'h-9 rounded-lg text-sm')}
                                  onChange={(event) =>
                                    updateDraft(index, { quantityLabel: event.target.value })
                                  }
                                />
                              </div>
                              <div className={FORM.fieldStack}>
                                <Label htmlFor={`service-max-quantity-${index}`}>Maximum</Label>
                                <Input
                                  id={`service-max-quantity-${index}`}
                                  type="number"
                                  min={1}
                                  value={draft.maxQuantity}
                                  className={cn(FORM.input, 'h-9 rounded-lg text-sm')}
                                  onChange={(event) =>
                                    updateDraft(index, {
                                      maxQuantity: Number(event.target.value) || 1,
                                    })
                                  }
                                />
                              </div>
                              <div className={FORM.fieldStack}>
                                <Label htmlFor={`service-additional-price-${index}`}>
                                  Each additional unit
                                </Label>
                                <ServiceUnitInput
                                  id={`service-additional-price-${index}`}
                                  value={formatCentsForInput(draft.pricePerAdditionalUnit)}
                                  prefix="$"
                                  onChange={(value) =>
                                    updateDraft(index, {
                                      pricePerAdditionalUnit: parseCurrencyToCents(value) ?? 0,
                                    })
                                  }
                                />
                              </div>
                              <div className={FORM.fieldStack}>
                                <Label htmlFor={`service-additional-duration-${index}`}>
                                  Extra time per unit
                                </Label>
                                <ServiceUnitInput
                                  id={`service-additional-duration-${index}`}
                                  type="number"
                                  min={0}
                                  value={draft.durationPerAdditionalUnit}
                                  suffix="min"
                                  onChange={(value) =>
                                    updateDraft(index, {
                                      durationPerAdditionalUnit: Number(value) || 0,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {catalogsQuery.error || catalogQuery.error || saveCatalogMutation.error ? (
          <p className="text-sm text-destructive">
            {(catalogsQuery.error ?? catalogQuery.error ?? saveCatalogMutation.error)?.message}
          </p>
        ) : null}
      </CardContent>
    </PremiumPanel>
  )
}

const ONBOARDING_INTEGRATIONS = [
  {
    id: 'google',
    name: 'Google Calendar',
    detail: 'Avoid double-booking existing events.',
    iconSrc: '/integrations/google-calendar.svg',
    iconAlt: 'Google Calendar',
  },
  {
    id: 'outlook',
    name: 'Outlook Calendar',
    detail: 'Respect Microsoft 365 holds.',
    iconSrc: '/integrations/outlook-calendar.svg',
    iconAlt: 'Microsoft Outlook',
  },
] as const

function IntegrationsSetupPanel() {
  const { data: connections = [] } = trpc.calendar.connections.list.useQuery()
  const configuredConnection = connections.find(
    (connection) => connection.status !== 'disconnected'
  )
  const connectMutation = trpc.calendar.connectUrl.useMutation({
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (error) => toast.error('Unable to connect calendar', { description: error.message }),
  })

  return (
    <div className={stepStackClass}>
      <PremiumPanel className={cn(setupPanelClass, 'gap-0 py-0')}>
        <CardContent className="p-4 md:p-5">
          <div className="grid gap-3 md:grid-cols-2">
            {ONBOARDING_INTEGRATIONS.map((integration) => {
              const provider = integration.id === 'outlook' ? 'microsoft' : 'google'
              const isCurrentProvider = configuredConnection?.provider === provider
              const isActive = configuredConnection?.status === 'active' && isCurrentProvider
              const needsReauth =
                configuredConnection?.status === 'needs_reauth' && isCurrentProvider
              const isBlocked = Boolean(configuredConnection && !isCurrentProvider)

              return (
                <Card
                  key={integration.name}
                  className="rounded-[4px] border-border/70 bg-background shadow-none"
                >
                  <CardContent className="flex min-h-[180px] flex-col justify-between gap-6 p-4 md:min-h-[204px] md:p-5">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[4px] border border-border/60 bg-background shadow-none">
                          <img
                            src={integration.iconSrc}
                            alt={integration.iconAlt}
                            className="h-8 w-8 object-contain"
                            loading="lazy"
                          />
                        </div>
                        <Badge
                          variant={isCurrentProvider ? 'secondary' : 'outline'}
                          className="gap-1 rounded-[3px] bg-background/80 shadow-none"
                        >
                          {isActive ? <CheckCircle2 className="size-3" /> : null}
                          {needsReauth ? <RefreshCw className="size-3" /> : null}
                          {isBlocked ? <LockKeyhole className="size-3" /> : null}
                          {isActive
                            ? 'Connected'
                            : needsReauth
                              ? 'Needs reauth'
                              : isBlocked
                                ? 'Unavailable'
                                : 'Recommended'}
                        </Badge>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <div className="text-base font-semibold text-foreground md:text-lg">
                          {integration.name}
                        </div>
                        <p className="text-sm leading-6 text-muted-foreground">
                          {isBlocked
                            ? 'Disconnect the current calendar before choosing another provider.'
                            : integration.detail}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full justify-center rounded-[3px]"
                      disabled={connectMutation.isPending || isActive || isBlocked}
                      onClick={() =>
                        connectMutation.mutate({
                          provider,
                          returnTo: window.location.href,
                        })
                      }
                    >
                      {isActive
                        ? 'Connected'
                        : needsReauth
                          ? 'Reconnect'
                          : isBlocked
                            ? 'Disconnect current first'
                            : 'Connect'}
                      {!isActive && !isBlocked ? <ArrowRight className="size-3.5" /> : null}
                    </Button>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </CardContent>
      </PremiumPanel>
    </div>
  )
}

export function OperatorSetupWizard({
  configuration,
  onSaved,
}: {
  configuration: OperatorConfigurationState
  readiness: ReadinessModel
  onSaved?: () => Promise<void> | void
}) {
  const router = useRouter()
  const [step, setStep] = useState<SetupWizardStepId>(() =>
    getInitialSetupWizardStep(configuration)
  )
  const [furthestStepIndex, setFurthestStepIndex] = useState(() => {
    const initialStep = getInitialSetupWizardStep(configuration)
    return Math.max(
      0,
      WIZARD_STEPS.findIndex((item) => item.id === initialStep)
    )
  })
  const businessSubmitRef = useRef<null | (() => Promise<boolean>)>(null)
  const coverageSubmitRef = useRef<null | (() => Promise<boolean>)>(null)
  const availabilitySubmitRef = useRef<null | (() => Promise<boolean>)>(null)
  const servicesSubmitRef = useRef<null | (() => Promise<boolean>)>(null)
  const [businessDraft, setBusinessDraft] = useState<BusinessProfileDraft | null>(null)
  const [coveragePreviewDraft, setCoveragePreviewDraft] = useState<CoveragePreviewDraft | null>(
    null
  )
  const [availabilityPreviewSlots, setAvailabilityPreviewSlots] = useState<
    AvailabilitySlotRecord[] | null
  >(null)
  const [servicePreviewRecords, setServicePreviewRecords] = useState<ServiceRecord[] | null>(null)
  const [isAdvancing, setIsAdvancing] = useState(false)
  const handleBusinessDraftChange = useCallback((draft: BusinessProfileDraft) => {
    setBusinessDraft((current) => (areBusinessDraftsEqual(current, draft) ? current : draft))
  }, [])

  const currentStepIndex = WIZARD_STEPS.findIndex((item) => item.id === step)
  const currentStepConfig = WIZARD_STEPS[currentStepIndex] ?? WIZARD_STEPS[0]
  const isLastStep = currentStepIndex === WIZARD_STEPS.length - 1
  const hasBusinessStep = Boolean(
    configuration.operator?.businessName && configuration.operator?.slug
  )
  const hasServicesStep = configuration.services.some((service) => service.isActive)
  const hasCoverageStep = Boolean(
    configuration.serviceArea?.centerLat && configuration.serviceArea?.centerLng
  )
  const hasAvailabilityStep = configuration.slots.length > 0
  const integrationsStepIndex = WIZARD_STEPS.findIndex((item) => item.id === 'integrations')

  const stepComplete: Record<SetupWizardStepId, boolean> = {
    business: hasBusinessStep,
    services: hasServicesStep,
    coverage: hasCoverageStep,
    availability: hasAvailabilityStep,
    integrations: furthestStepIndex > integrationsStepIndex,
  }

  const canAdvance = stepComplete[step]

  async function move(direction: 'back' | 'next') {
    if (isAdvancing) return

    if (direction === 'next') {
      try {
        setIsAdvancing(true)

        if (step === 'business') {
          const ok = await businessSubmitRef.current?.()
          if (!ok) return
        }

        if (step === 'availability') {
          const ok = await availabilitySubmitRef.current?.()
          if (!ok) return
        }

        if (step === 'coverage') {
          const ok = await coverageSubmitRef.current?.()
          if (!ok) return
        }

        if (step === 'services') {
          const ok = await servicesSubmitRef.current?.()
          if (!ok) return
        }
      } finally {
        setIsAdvancing(false)
      }
    }

    const nextIndex = direction === 'back' ? currentStepIndex - 1 : currentStepIndex + 1
    const nextStep = WIZARD_STEPS[nextIndex]
    if (nextStep) {
      setStep(nextStep.id)
      setFurthestStepIndex((current) => Math.max(current, nextIndex))
      return
    }

    if (direction === 'next' && isLastStep) {
      router.navigate({ to: '/', replace: true })
    }
  }

  const nextAction = getSetupWizardNextAction({
    step,
    canAdvance,
    isLastStep,
  })
  const nextBlocked = nextAction.disabled || isAdvancing
  const nextLabel = nextAction.label
  const pinBottomAction = step === 'business' || step === 'coverage' || step === 'availability'
  const showPreviewView = true
  const previewConfiguration = useMemo<OperatorConfigurationState>(() => {
    const baseOperator = configuration.operator
    const previewOperator: OperatorProfile | null = businessDraft
      ? {
          id: baseOperator?.id ?? 'draft-operator',
          businessName: businessDraft.businessName,
          slug: businessDraft.slug,
          bio: businessDraft.bio,
          phone: businessDraft.phone,
          timezone:
            businessDraft.timezone ||
            baseOperator?.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone ||
            'America/Los_Angeles',
          logoUrl: businessDraft.logoUrl ?? baseOperator?.logoUrl ?? null,
          isActive: baseOperator?.isActive ?? false,
          artifactPublicationState: baseOperator?.artifactPublicationState ?? 'draft',
          launchState: baseOperator?.launchState ?? 'onboarding_required',
          selectedBillingModel: baseOperator?.selectedBillingModel ?? null,
          expectedMonthlyBookings: baseOperator?.expectedMonthlyBookings ?? null,
          bookingPageTheme: baseOperator?.bookingPageTheme ?? null,
          goLiveRequestedAt: baseOperator?.goLiveRequestedAt ?? null,
          wentLiveAt: baseOperator?.wentLiveAt ?? null,
        }
      : configuration.operator

    return {
      ...configuration,
      operator: previewOperator,
      slots: availabilityPreviewSlots ?? configuration.slots,
      services: servicePreviewRecords ?? configuration.services,
    }
  }, [availabilityPreviewSlots, businessDraft, configuration, servicePreviewRecords])

  return (
    <div
      className={cn(
        'setup-flow-shell min-h-[100dvh] w-full bg-background',
        showPreviewView ? 'xl:grid xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]' : ''
      )}
    >
      <div
        className={cn(
          'setup-editor-column relative flex h-[100dvh] min-h-[100dvh] flex-col overflow-hidden',
          showPreviewView ? 'xl:border-r xl:border-border/60' : ''
        )}
      >
        <section className="setup-editor-pane relative min-h-0 flex-1 overflow-y-auto">
          <div
            className={cn(
              SHELL.pagePadding,
              showPreviewView ? 'max-w-4xl xl:max-w-none' : 'max-w-4xl'
            )}
          >
            <SetupShellHeader
              title={currentStepConfig.title}
              description={currentStepConfig.description}
              currentStepIndex={currentStepIndex}
              furthestStepIndex={furthestStepIndex}
              stepComplete={stepComplete}
              onStepSelect={setStep}
            />

            <main
              className={cn(
                'min-w-0',
                pinBottomAction ? 'flex min-h-0 flex-1 flex-col pb-6 md:pb-8' : 'pb-6 md:pb-8'
              )}
            >
              <div
                className={cn(
                  stepStackClass,
                  step === 'availability' ? 'flex min-h-0 flex-1 flex-col' : ''
                )}
              >
                {step === 'business' ? (
                  <BusinessProfileEditor
                    configuration={configuration}
                    onSaved={onSaved}
                    submitLabel="Continue"
                    pendingSubmitLabel="Saving…"
                    hideSubmitButton
                    hideHeader
                    onRegisterSubmit={(submit) => {
                      businessSubmitRef.current = submit
                    }}
                    onDraftChange={handleBusinessDraftChange}
                  />
                ) : null}
                {step === 'availability' ? (
                  <AvailabilityEditor
                    configuration={configuration}
                    onSaved={onSaved}
                    hidePrimarySubmit
                    onRegisterSubmit={(submit) => {
                      availabilitySubmitRef.current = submit
                    }}
                    onPreviewChange={setAvailabilityPreviewSlots}
                  />
                ) : null}
                {step === 'coverage' ? (
                  <ServiceFootprintEditor
                    configuration={configuration}
                    onboardingMode
                    onSaved={onSaved}
                    onPreviewChange={setCoveragePreviewDraft}
                    onRegisterSubmit={(submit) => {
                      coverageSubmitRef.current = submit
                    }}
                  />
                ) : null}
                {step === 'services' ? (
                  <ServicesPricingEditor
                    configuration={configuration}
                    onSaved={onSaved}
                    onPreviewServicesChange={setServicePreviewRecords}
                    onRegisterSubmit={(submit) => {
                      servicesSubmitRef.current = submit
                    }}
                  />
                ) : null}
                {step === 'integrations' ? <IntegrationsSetupPanel /> : null}
              </div>
            </main>
          </div>
        </section>

        <div
          className={cn(
            SHELL.footer.base,
            currentStepIndex === 0 ? 'flex justify-end' : 'flex justify-between',
            pinBottomAction ? SHELL.footer.pinned : 'px-4 py-2 md:px-6 md:py-3',
            'max-md:flex-col-reverse max-md:items-stretch max-md:gap-3'
          )}
        >
          {currentStepIndex > 0 ? (
            <Button
              variant="ghost"
              className={cn(
                'text-muted-foreground max-md:w-full max-md:justify-center',
                pinBottomAction ? SHELL.footer.compactButton : 'max-md:h-11'
              )}
              onClick={() => void move('back')}
              disabled={isAdvancing}
            >
              Back
            </Button>
          ) : null}
          <Button
            className={cn(
              pinBottomAction ? SHELL.footer.compactButton : 'max-md:h-11',
              'max-md:w-full max-md:justify-center'
            )}
            onClick={() => void move('next')}
            disabled={nextBlocked}
            aria-busy={isAdvancing}
          >
            {isAdvancing ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Saving
              </span>
            ) : (
              nextLabel
            )}
          </Button>
        </div>
      </div>

      {showPreviewView ? (
        <aside className={SHELL.preview.aside}>
          <div className={SHELL.preview.frame}>
            <div className={SHELL.preview.header}>
              <div className={SHELL.preview.label}>Preview</div>
              <div className={SHELL.preview.subLabel}>
                {step === 'coverage' ? 'Coverage view.' : 'Customer view.'}
              </div>
            </div>
            <div className={SHELL.preview.body}>
              {step === 'coverage' ? (
                <CoveragePreviewCard
                  configuration={configuration}
                  draft={coveragePreviewDraft}
                  showSummary={false}
                  showFrame={false}
                  className="h-full"
                  mapClassName="h-full"
                />
              ) : (
                <BookingPreviewCard configuration={previewConfiguration} />
              )}
            </div>
          </div>
        </aside>
      ) : null}
    </div>
  )
}
